import type { QueryKey } from "@tanstack/react-query";
import type { QueryScope } from "@/queries/scope";
import type { THintTarget, THintTargets } from "./types";

/**
 * A project's panels' hint rows (NG-2795): its comments, activity, linked
 * requests, files, roster and logged time, each read only through Query. A
 * hint names the domain for the whole workspace, so a row marks every
 * project's copy stale, and re-reads only the ones on screen.
 *
 * Each read sits under `[slug, uid, 'project', id, <resource>, …]`, with the
 * project id in the middle, so the rows match by the resource rather than by
 * a key prefix. Logged time also lives under its own domain
 * (`[slug, uid, 'worklogs', …]`, `queries/worklogs.ts`), and the cost summary
 * follows it: actual cost is logged time priced.
 */
function projectPanelRead(
  resources: readonly string[],
  domains: readonly string[] = [],
): THintTarget {
  const matches =
    (scope: QueryScope) =>
    ({ queryKey }: { queryKey: QueryKey }) =>
      queryKey[0] === scope.tenant &&
      queryKey[1] === scope.principal &&
      ((queryKey[2] === "project" && resources.includes(queryKey[4] as string)) ||
        domains.includes(queryKey[2] as string));
  return {
    mark: (_root, _slug, { client, scope }) =>
      void client.invalidateQueries({ predicate: matches(scope), refetchType: "none" }),
    reread: (_root, _slug, { client, scope }) =>
      void client.refetchQueries({ predicate: matches(scope), type: "active", stale: true }),
  };
}

export const PROJECT_PANELS_HINTS: THintTargets = {
  "project-comments:list": projectPanelRead(["comments"]),
  "project-activity:list": projectPanelRead(["activities"]),
  "project-requests:list": projectPanelRead(["linked-submissions"]),
  "project-files:list": projectPanelRead(["attachments"]),
  "project-members:list": projectPanelRead(["members", "member-me"]),
  "worklogs:list": projectPanelRead(["worklogs", "cost-summary"], ["worklogs"]),
};
